import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Save, Link2, DollarSign, Settings2 } from "lucide-react";

const clone = (o) => JSON.parse(JSON.stringify(o));

export const ProgramConfigEditor = () => {
  const [configs, setConfigs] = useState([]);
  const [products, setProducts] = useState([]);
  const [savingKey, setSavingKey] = useState("");

  useEffect(() => {
    api.get("/admin/program-configs").then((r) => setConfigs(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const priceOf = (pid) => {
    const p = products.find((x) => x.id === pid);
    return p ? Number(p.price) : null;
  };

  const patch = (ci, fn) => {
    setConfigs((prev) => {
      const next = clone(prev);
      fn(next[ci]);
      return next;
    });
  };

  const normalizeQuestions = (questions) =>
    (questions || []).map((q) => ({
      ...q,
      options: (q.options || []).map((o) => {
        const opt = { ...o };
        if (Object.prototype.hasOwnProperty.call(opt, "unit_price")) {
          opt.unit_price = Number(opt.unit_price) || 0;
          if (!opt.product_id) {
            const base = (opt.label_base || String(opt.label || "").split(" — ")[0]).trim() || "Tier";
            opt.label_base = base;
            opt.label = `${base} — $${Math.round(opt.unit_price)}/box`;
          }
        }
        if (Object.prototype.hasOwnProperty.call(opt, "addon")) opt.addon = Number(opt.addon) || 0;
        if (Object.prototype.hasOwnProperty.call(opt, "qty")) opt.qty = Number(opt.qty) || 0;
        return opt;
      }),
    }));

  const save = async (cfg) => {
    setSavingKey(cfg.key);
    const questions = normalizeQuestions(cfg.questions);
    const threshold = Number(cfg.threshold) || 0;
    setConfigs((prev) => prev.map((c) => (c.key === cfg.key ? { ...c, questions, threshold } : c)));
    try {
      await api.put(`/admin/program-config/${cfg.key}`, {
        title: cfg.title || "",
        intro: cfg.intro || "",
        active: cfg.active !== false,
        threshold,
        questions,
      });
      toast.success(`${cfg.title || cfg.key} pricing saved 💛`);
    } catch {
      toast.error("Could not save program.");
    } finally {
      setSavingKey("");
    }
  };

  return (
    <div className="space-y-6" data-testid="program-config-editor">
      <div className="bg-[#8E9A86]/10 border border-[#8E9A86]/25 rounded-xl px-5 py-4">
        <h3 className="font-heading text-lg text-[#3A3632] flex items-center gap-2"><Settings2 className="h-4 w-4" /> Program Pricing &amp; Questions</h3>
        <p className="text-sm text-[#6B655F] mt-1">Edit the tiers, prices and questions for each B2B program — no redeploy needed. Link a tier to a product so its price updates automatically whenever you change that product.</p>
      </div>

      {configs.map((cfg, ci) => (
        <div key={cfg.key} className="bg-white rounded-xl border border-[#E5DFD5] p-6 space-y-5" data-testid={`pc-card-${cfg.key}`}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h4 className="font-heading text-xl text-[#3A3632]">{cfg.title || cfg.key}</h4>
            <label className="flex items-center gap-2 text-sm text-[#3A3632]">
              <input type="checkbox" checked={cfg.active !== false} onChange={(e) => patch(ci, (c) => { c.active = e.target.checked; })}
                className="h-4 w-4 accent-[#8E9A86]" data-testid={`pc-active-${cfg.key}`} />
              Visible on site
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-medium text-[#6B655F]">Program title</span>
              <input value={cfg.title || ""} onChange={(e) => patch(ci, (c) => { c.title = e.target.value; })}
                className="ai mt-1" data-testid={`pc-title-${cfg.key}`} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-[#6B655F]">Quote-vs-checkout threshold ($)</span>
              <input type="number" min="0" value={cfg.threshold ?? 0} onChange={(e) => patch(ci, (c) => { c.threshold = e.target.value; })}
                className="ai mt-1" data-testid={`pc-threshold-${cfg.key}`} />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-medium text-[#6B655F]">Intro line (shown above the estimator)</span>
            <textarea rows={2} value={cfg.intro || ""} onChange={(e) => patch(ci, (c) => { c.intro = e.target.value; })}
              className="ai mt-1 resize-none" data-testid={`pc-intro-${cfg.key}`} />
          </label>

          <div className="space-y-4">
            {(cfg.questions || []).map((q, qi) => (
              <div key={q.id} className="border border-[#EDE8DF] rounded-lg p-4">
                <div className="text-sm font-medium text-[#3A3632] mb-3">{q.label} <span className="text-[#B4A99A] font-normal">({q.id})</span></div>
                <div className="space-y-2.5">
                  {(q.options || []).map((o, oi) => {
                    const linked = !!o.product_id;
                    const hasUnit = Object.prototype.hasOwnProperty.call(o, "unit_price");
                    const hasAddon = Object.prototype.hasOwnProperty.call(o, "addon");
                    const hasQty = Object.prototype.hasOwnProperty.call(o, "qty");
                    return (
                      <div key={o.value} className="grid md:grid-cols-[1fr,auto] gap-2 items-center bg-[#FAF8F4] rounded-md px-3 py-2">
                        {/* label / tier name */}
                        <input
                          value={linked ? (o.label_base || String(o.label || "").split(" — ")[0]) : (o.label || "")}
                          onChange={(e) => patch(ci, (c) => {
                            const opt = c.questions[qi].options[oi];
                            if (linked) opt.label_base = e.target.value; else opt.label = e.target.value;
                          })}
                          className="ai !py-1.5 text-sm"
                          placeholder={linked ? "Tier name (price added automatically)" : "Option label"}
                          data-testid={`pc-label-${cfg.key}-${q.id}-${o.value}`}
                        />

                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          {hasUnit && (
                            <>
                              <div className="relative">
                                <DollarSign className="h-3.5 w-3.5 text-[#8A8175] absolute left-2 top-1/2 -translate-y-1/2" />
                                <input type="number" min="0" step="0.01" disabled={linked}
                                  value={linked ? (priceOf(o.product_id) ?? o.unit_price ?? 0) : (o.unit_price ?? 0)}
                                  onChange={(e) => patch(ci, (c) => { c.questions[qi].options[oi].unit_price = e.target.value; })}
                                  className={`ai !py-1.5 !pl-7 w-28 text-sm ${linked ? "opacity-60" : ""}`}
                                  title={linked ? "Auto-priced from linked product" : "Price per box"}
                                  data-testid={`pc-price-${cfg.key}-${q.id}-${o.value}`} />
                              </div>
                              <span className="text-xs text-[#8A8175]">/box</span>
                              <div className="relative">
                                <Link2 className="h-3.5 w-3.5 text-[#8E9A86] absolute left-2 top-1/2 -translate-y-1/2" />
                                <select
                                  value={o.product_id || ""}
                                  onChange={(e) => patch(ci, (c) => {
                                    const opt = c.questions[qi].options[oi];
                                    if (e.target.value) {
                                      opt.product_id = e.target.value;
                                      if (!opt.label_base) opt.label_base = String(opt.label || "").split(" — ")[0];
                                    } else {
                                      delete opt.product_id;
                                    }
                                  })}
                                  className="ai !py-1.5 !pl-7 text-sm max-w-[190px]"
                                  data-testid={`pc-link-${cfg.key}-${q.id}-${o.value}`}>
                                  <option value="">Manual price</option>
                                  {products.map((p) => (
                                    <option key={p.id} value={p.id}>🔗 {p.name} (${Number(p.price).toFixed(0)})</option>
                                  ))}
                                </select>
                              </div>
                            </>
                          )}
                          {hasAddon && (
                            <div className="relative">
                              <DollarSign className="h-3.5 w-3.5 text-[#8A8175] absolute left-2 top-1/2 -translate-y-1/2" />
                              <input type="number" min="0" step="1" value={o.addon ?? 0}
                                onChange={(e) => patch(ci, (c) => { c.questions[qi].options[oi].addon = e.target.value; })}
                                className="ai !py-1.5 !pl-7 w-28 text-sm" title="One-time add-on"
                                data-testid={`pc-addon-${cfg.key}-${q.id}-${o.value}`} />
                            </div>
                          )}
                          {hasQty && (
                            <input type="number" min="1" value={o.qty ?? 1}
                              onChange={(e) => patch(ci, (c) => { c.questions[qi].options[oi].qty = e.target.value; })}
                              className="ai !py-1.5 w-24 text-sm" title="Box quantity for this tier"
                              data-testid={`pc-qty-${cfg.key}-${q.id}-${o.value}`} />
                          )}
                          {!hasUnit && !hasAddon && !hasQty && (
                            <span className="text-xs text-[#B4A99A] italic">no price</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <button onClick={() => save(cfg)} disabled={savingKey === cfg.key}
            className="bg-[#8E9A86] text-white rounded-full px-6 py-2.5 font-medium hover:bg-[#7A8772] inline-flex items-center gap-2 disabled:opacity-60"
            data-testid={`pc-save-${cfg.key}`}>
            <Save className="h-4 w-4" /> {savingKey === cfg.key ? "Saving…" : "Save program"}
          </button>
        </div>
      ))}
    </div>
  );
};
